import React, { useMemo } from "react";
import { FieldArray, useFormikContext, getIn } from "formik";
import { useQuery } from "@tanstack/react-query";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { getDisciplinesByProfileType } from "components/FormInputs/ExpertiseDisciplines/expertiseDisciplinesAPI";
import { ExpertiseSelect } from "newui/components/ui/CommonSelects/ExpertiseSelect";
import { DisciplineSelect } from "newui/components/ui/CommonSelects/DisciplineSelect";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { DisciplineGroup } from "../types/KeyPersonnel.type";

interface ExpertiseRepeaterProps {
	name: string;
	profileTypeId: number;
}

interface DisciplineRecord {
	id: number;
	name: string;
	created_at: string;
	expertise_id: number;
	profile_type_id: number;
	updated_at: string;
}

interface ExpertiseOption {
	id: number;
	name: string;
}

export const ExpertiseRepeater: React.FC<ExpertiseRepeaterProps> = ({
	name,
	profileTypeId,
}) => {
	const { values, setFieldValue } = useFormikContext<Record<string, DisciplineGroup[]>>();
	const rows: DisciplineGroup[] = getIn(values, name) ?? [];

	const { data, isLoading } = useQuery({
		queryKey: ["disciplines-by-profile-type", profileTypeId],
		queryFn: () => getDisciplinesByProfileType(profileTypeId),
		enabled: profileTypeId > 0,
		staleTime: 5 * 60 * 1000,
	});

	const { expertiseOptions, disciplinesByExpertise } = useMemo(() => {
		const expertiseMap = new Map<string, ExpertiseOption>();
		const discMap = new Map<string, { id: number; name: string }[]>();

		if (!data?.expertiseDisciplinesByProfile) {
			return { expertiseOptions: [], disciplinesByExpertise: discMap };
		}

		const grouped = data.expertiseDisciplinesByProfile;
		Object.entries(grouped).forEach(([expertiseName, group]) => {
			const expertiseId = group.expertise_id;
			expertiseMap.set(expertiseName, { id: expertiseId, name: expertiseName });

			const disciplines: { id: number; name: string }[] = [];
			Object.entries(group).forEach(([key, val]) => {
				if (key !== "expertise_id" && typeof val === "object" && val !== null) {
					const disc = val as DisciplineRecord;
					disciplines.push({ id: disc.id, name: disc.name });
				}
			});
			discMap.set(String(expertiseId), disciplines);
		});

		return {
			expertiseOptions: Array.from(expertiseMap.values()),
			disciplinesByExpertise: discMap,
		};
	}, [data]);

	const handleExpertiseChange = (index: number, selected: SelectOption | SelectOption[] | null) => {
		const label = selected && !Array.isArray(selected) ? String(selected.label) : "";
		const value = selected && !Array.isArray(selected) ? String(selected.value) : "";
		setFieldValue(`${name}.${index}.expertiseId`, value);
		setFieldValue(`${name}.${index}.expertise`, label);
		setFieldValue(`${name}.${index}.disciplines`, []);
	};

	const handleDisciplinesChange = (index: number, selected: SelectOption | SelectOption[] | null) => {
		const ids = Array.isArray(selected) ? selected.map((s) => Number(s.value)) : [];
		setFieldValue(`${name}.${index}.disciplines`, ids);
	};

	const getDisciplineOptions = (expertiseId: number) => {
		if (!expertiseId) {
			return [];
		}
		return disciplinesByExpertise.get(expertiseId.toString()) ?? [];
	};

	const emptyRow: DisciplineGroup = { expertise: "", expertiseId: null, disciplines: [] };

	return (
		<FieldArray name={name}>
			{({ push, remove }) => (
				<div className="flex flex-col gap-4" data-testid="expertise-repeater">
					{rows.map((row, index) => {
						const expertiseValue = row.expertiseId
							? { value: Number(row.expertiseId), label: expertiseOptions.find((e) => e.id === row.expertiseId)?.name ?? row.expertise }
							: undefined;

						const disciplineOpts = getDisciplineOptions(row.expertiseId);
						const disciplineValue = row.disciplines.map((dId) => {
							const disc = disciplineOpts.find((d) => d.id === dId);
							return { value: dId, label: disc?.name ?? String(dId) };
						});

						return (
							<div
								key={index}
								className="flex items-start gap-3"
								data-testid={`expertise-row-${index}`}
							>
								<div className="flex-1">
									<ExpertiseSelect
										name={`${name}.${index}.expertiseId`}
										options={expertiseOptions}
										value={expertiseValue}
										onChange={(selected) => handleExpertiseChange(index, selected)}
										isDisabled={isLoading}
									/>
								</div>
								<div className="flex-1">
									<DisciplineSelect
										name={`${name}.${index}.disciplines`}
										options={disciplineOpts}
										value={!isLoading && disciplineValue}
										onChange={(selected) => handleDisciplinesChange(index, selected)}
										isDisabled={isLoading || !row.expertiseId}
										isLoading={isLoading}
										placeholder={row.expertiseId ? "Select disciplines" : "Select expertise first"}
									/>
								</div>
								<button
									type="button"
									onClick={() => remove(index)}
									className="mt-8 rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-500"
									aria-label="Remove expertise row"
									data-testid={`remove-expertise-${index}`}
								>
									<FiTrash2 size={18} />
								</button>
							</div>
						);
					})}
					<button
						type="button"
						onClick={() => push(emptyRow)}
						className="flex items-center gap-2 self-start rounded border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600"
						data-testid="add-expertise-btn"
					>
						<FiPlus size={16} />
						Add Expertise
					</button>
				</div>
			)}
		</FieldArray>
	);
};
